import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { map as _map } from 'lodash'
import { BehaviorSubject, merge, Observable, of, ReplaySubject, Subject } from 'rxjs'
import { catchError, concatMap, exhaustMap, filter, map, scan, share, withLatestFrom } from 'rxjs/operators'

import { AppointmentRecModel } from '../model/appointment-rec'
import { Patient } from '../model/patient'
import { AuthService } from './auth.service'
import { AuthenticatedRestService } from './auth-rest.service'
import { ClinicService } from './clinic.service'
import { LoadingService } from './loading.service'
import { ActionType, isError, isResponse, toData, updateCollection } from './rest'

@Injectable()
export class AppointmentRecService extends AuthenticatedRestService {
  private appointmentRecItem = new Subject<AppointmentRecModel>()

  route = '/appointment-recommendations'
  loader$ = new BehaviorSubject(false)
  patientSelectedAction = new ReplaySubject<Patient>()
  actionType = new ReplaySubject<ActionType>()
  actionType$ = this.actionType.asObservable()
  updateParams = new ReplaySubject<Record<string, any>>(1)
  loading$: Observable<boolean>
  appointmentRecItem$ = this.appointmentRecItem.asObservable()

  constructor(
    protected http: HttpClient,
    protected clinicService: ClinicService,
    protected authService: AuthService,
    loadingService: LoadingService) {
    super(http, clinicService, authService)
    this.loading$ = loadingService.isLoading(this.route)
  }

  appointmentsRecResponse$ = this.patientSelectedAction.pipe(
    map(patient => new HttpParams({ fromObject: { patientId: patient.id } })),
    concatMap((params) => this.get<AppointmentRecModel[]>(this.route, params).pipe(
      catchError((error: HttpErrorResponse) => of(error)),
    )),
    share(),
  )

  appointmentRecSuccesse$ = this.appointmentsRecResponse$.pipe(
    filter(isResponse),
    map(toData),
    map(res => _map(res, (res) => new AppointmentRecModel(res))),
    share(),
  )

  appointmentRecError$ = this.appointmentsRecResponse$.pipe(
    filter(isError),
    map(error => error.message),
  )

  updateResponse$ = this.updateParams.pipe(
    withLatestFrom(this.actionType),
    exhaustMap(([params, action]) => this.postCrud(params, action).pipe(
      catchError((error: HttpErrorResponse) => of(error)),
    )),
    share(),
  )

  updateResponseSuccesse$ = this.updateResponse$.pipe(
    filter(isResponse),
    map(toData),
    withLatestFrom(this.actionType),
    map(([res, action]) => {
      return { item: new AppointmentRecModel(res), action }
    }),
  )

  updateResponseError$ = this.updateResponse$.pipe(
    filter(isError),
    map(res => res.message),
  )

  appointmentsRecWithAdd$ = merge(
    this.appointmentRecSuccesse$,
    this.updateResponseSuccesse$,
  ).pipe(
    scan((acc, value) => (value instanceof Array) ? [...value] : updateCollection<AppointmentRecModel>(acc, value), [] as AppointmentRecModel[]),
  )

  private postCrud = (params: Record<string, any>, action: ActionType) => {
    if (action === 'delete') {
      return this.delete<AppointmentRecModel>(`${this.route}/${params.id}`, params)
    } else if (action === 'update') {
      return this.put<AppointmentRecModel>(`${this.route}/${params.id}`, params)
    } else {
      return this.post<AppointmentRecModel>(this.route, params)
    }
  }
}
